import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { _postBaoLoiSanPham, _uploadFile } from '../../../api/baoHanh';

const MAX_IMAGES = 5;

const schema = yup.object({
  tenKhach: yup.string().required('Vui lòng nhập tên'),
  soDienThoai: yup
    .string()
    .matches(/^[0-9]{10,11}$/, 'Số điện thoại không hợp lệ')
    .required('Vui lòng nhập số điện thoại'),
  model: yup.string().required('Vui lòng nhập model sản phẩm'),
  moTaLoi: yup.string().min(20, 'Mô tả ít nhất 20 ký tự').required('Vui lòng mô tả lỗi'),
  serial: yup.string(),
});

const BaoLoiModal = ({ open, onClose, model }) => {
  const [previewImages, setPreviewImages] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { model: model?.MA_DIEU_CHINH || '' },
  });

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setPreviewImages((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.url));
        return [];
      });
      reset({ tenKhach: '', soDienThoai: '', model: model?.MA_DIEU_CHINH || '', serial: '', moTaLoi: '' });
    }
  }, [open, model, reset]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setPreviewImages((prev) => {
      const remaining = MAX_IMAGES - prev.length;
      if (remaining <= 0) {
        toast.warning(`Chỉ được tải lên tối đa ${MAX_IMAGES} ảnh`);
        return prev;
      }
      const newPreviews = files.slice(0, remaining).map((file) => ({ file, url: URL.createObjectURL(file), name: file.name }));
      if (files.length > remaining) toast.warning(`Chỉ được tải lên tối đa ${MAX_IMAGES} ảnh`);
      return [...prev, ...newPreviews];
    });
    e.target.value = '';
  };

  const removeImage = (index) => {
    setPreviewImages((prev) => {
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const onSubmit = async (data) => {
    let hinhAnh = [];
    if (previewImages.length) {
      const uploaded = await _uploadFile('BaoLoiSanPham', { files: previewImages.map((p) => p.file) }, 'GigaCare');
      if (!uploaded) return;
      hinhAnh = uploaded.map((u) => u?.linkFile).filter(Boolean);
    }

    const body = {
      KhachHang: data.tenKhach,
      SoDienThoai: data.soDienThoai,
      MaHang: model.MA_HANG,
      Serial: data.serial || '',
      MoTa: data.moTaLoi,
      hinhAnh,
    };

    const res = await _postBaoLoiSanPham(body);
    if (res) {
      previewImages.forEach((p) => URL.revokeObjectURL(p.url));
      setPreviewImages([]);
      setSubmitted(true);
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 800 }}>
        <div className="modal-content rounded-4 border-0 shadow">
          {/* Header */}
          <div className="modal-header border-bottom px-4 py-3">
            <div className="d-flex align-items-center gap-2">
              <div className="d-flex align-items-center justify-content-center rounded-3 bg-danger bg-opacity-10" style={{ width: 36, height: 36 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div>
                <h4 className="modal-title fw-bold mb-0" style={{ fontSize: 18 }}>
                  Báo lỗi sản phẩm
                </h4>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit(onSubmit)}>
            {submitted ? (
              <div className="modal-body px-4 py-5 text-center">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 mx-auto mb-3"
                  style={{ width: 64, height: 64 }}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#198754" strokeWidth="2">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </div>
                <h5 className="fw-bold mb-2">Gửi báo lỗi thành công</h5>
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  Chúng tôi đã ghi nhận thông tin, đội ngũ hỗ trợ kỹ thuật sẽ liên hệ với bạn sớm nhất.
                </p>
              </div>
            ) : (
              <div className="modal-body px-4 py-3">
                <div className="row g-3">
                {/* Tên */}
                <div className="col-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Tên khách hàng <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register('tenKhach')}
                    className={`form-control form-control-sm ${errors.tenKhach ? 'is-invalid' : ''}`}
                    placeholder="Điền họ tên của bạn"
                  />
                  {errors.tenKhach && <div className="invalid-feedback">{errors.tenKhach.message}</div>}
                </div>

                {/* SĐT */}
                <div className="col-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Số điện thoại <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register('soDienThoai')}
                    className={`form-control form-control-sm ${errors.soDienThoai ? 'is-invalid' : ''}`}
                    placeholder="Điền SĐT của bạn"
                  />
                  {errors.soDienThoai && <div className="invalid-feedback">{errors.soDienThoai.message}</div>}
                </div>

                {/* Model */}
                <div className="col-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Model <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register('model')}
                    className={`form-control form-control-sm ${errors.model ? 'is-invalid' : ''}`}
                    placeholder="Điền mã model đang gặp vấn đề"
                    readOnly
                  />
                  {errors.model && <div className="invalid-feedback">{errors.model.message}</div>}
                </div>

                {/* Serial */}
                <div className="col-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Serial{' '}
                    <span className="text-muted fw-normal" style={{ fontSize: 11 }}>
                      (không bắt buộc)
                    </span>
                  </label>
                  <input {...register('serial')} className="form-control form-control-sm" placeholder="Điền serial của model" />
                </div>

                {/* Mô tả lỗi */}
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Mô tả lỗi <span className="text-danger">*</span>
                  </label>
                  <textarea
                    {...register('moTaLoi')}
                    className={`form-control form-control-sm ${errors.moTaLoi ? 'is-invalid' : ''}`}
                    rows={10}
                    placeholder="Mô tả chi tiết lỗi bạn gặp phải..."
                  />
                  {errors.moTaLoi && <div className="invalid-feedback">{errors.moTaLoi.message}</div>}
                </div>

                {/* Hình ảnh */}
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Hình ảnh model{' '}
                    <span className="text-muted fw-normal" style={{ fontSize: 11 }}>
                      (tối đa {MAX_IMAGES} ảnh)
                    </span>
                  </label>
                  <label
                    className="d-flex align-items-center rounded-3 gap-2 px-3 py-2"
                    style={{
                      border: '1.5px dashed #dee2e6',
                      background: previewImages.length >= MAX_IMAGES ? '#f1f3f5' : '#f8f9fa',
                      cursor: previewImages.length >= MAX_IMAGES ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <span className="text-muted" style={{ fontSize: 13 }}>
                      Up ảnh
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="d-none"
                      disabled={previewImages.length >= MAX_IMAGES}
                      onChange={handleImageChange}
                    />
                  </label>

                  {previewImages.length > 0 && (
                    <div className="d-flex mt-2 flex-wrap gap-2">
                      {previewImages.map((img, index) => (
                        <div
                          key={index}
                          className="position-relative rounded-3 overflow-hidden"
                          style={{ width: 72, height: 72, border: '1px solid #dee2e6' }}
                        >
                          <img src={img.url} alt={img.name} className="w-100 h-100" style={{ objectFit: 'cover' }} />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="position-absolute d-flex align-items-center justify-content-center rounded-circle end-0 top-0 border-0"
                            style={{ width: 20, height: 20, background: 'rgba(0,0,0,0.55)', margin: 3, padding: 0, cursor: 'pointer' }}
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              </div>
            )}

            {/* Footer */}
            <div className="modal-footer border-top gap-2 px-4 py-3">
              {submitted ? (
                <button type="button" className="btn btn-primary flex-fill fw-bold" onClick={onClose}>
                  Xác nhận
                </button>
              ) : (
                <>
                  <button type="button" className="btn btn-light flex-fill" onClick={onClose}>
                    Hủy
                  </button>
                  <button type="submit" className="btn btn-primary flex-fill fw-bold" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Đang gửi...
                      </>
                    ) : (
                      'Gửi báo lỗi'
                    )}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BaoLoiModal;
